// Central catalog data for DangerSky
// All images below are self-hosted (served from this app's own /images folder) —
// no external dependency on any third-party platform.
export const HERO_BG = "/images/hero-bg.jpg";
export const IMG_APPAREL = "/images/apparel.jpg";
export const IMG_STICKERS = "/images/stickers.jpg";
export const IMG_BUTTONS = "/images/buttons.jpg";
export const IMG_WOODWORK = "/images/woodworking.jpg";
export const IMG_LASER = "/images/laser-engraving.jpg";

// Default tile photos for built-in subcategories. The owner can override any of
// these by uploading a photo in Admin → Manage; that upload takes precedence.
export const DEFAULT_SUB_IMAGES = {
    "apparel/t-shirts": "/images/apparel-tshirts.jpg",
    "apparel/sweatshirts": "/images/apparel-sweatshirts.jpg",
    "apparel/hoodies": "/images/apparel-hoodies.jpg",
    "apparel/sweatpants": "/images/apparel-sweatpants.jpg",
    "apparel/hats": "/images/apparel-hats.jpg",
};

export const CONTACT_EMAIL = "dangerskycollectibles215@yahoo.com";

// Product types drive what the listing card / detail page looks like
// shirt: adult+kids size dropdowns, base+upcharges
// apparel-single: adult size only (hoodies, sweatshirts, sweatpants)
// hat: single price, no size
// sticker-pack: pack pricing
// single-item: just price (vinyl decal, bumper sticker, button, misc sticker)
// woodworking: price + "different size?" quote CTA
// laser-engraving: price + custom-engraving CTA
// custom: only the custom-engraving form (no fixed price)
export const PRODUCT_TYPES = {
    SHIRT: "shirt",
    APPAREL_SINGLE: "apparel-single",
    HAT: "hat",
    STICKER_PACK: "sticker-pack",
    SINGLE_ITEM: "single-item",
    WOODWORKING: "woodworking",
    LASER_ENGRAVING: "laser-engraving",
};

export const CATEGORIES = [
    {
        slug: "apparel",
        name: "Apparel",
        tagline: "Wear the storm.",
        image: IMG_APPAREL,
        description:
            "Heavyweight tees, hoodies, sweatshirts, sweatpants, and hats — printed, custom-designed, or fully personalized. Bold graphics, premium fabrics, built to outlast the weather.",
        sub: [
            { slug: "t-shirts", name: "T-Shirts", blurb: "Custom-printed cotton tees, adult & kids sizes.", productType: PRODUCT_TYPES.SHIRT },
            { slug: "hats", name: "Hats", blurb: "Snapbacks, beanies, trucker caps, dad hats.", productType: PRODUCT_TYPES.HAT },
            { slug: "sweatshirts", name: "Sweatshirts", blurb: "Crewneck classics with custom prints.", productType: PRODUCT_TYPES.APPAREL_SINGLE },
            { slug: "hoodies", name: "Hoodies", blurb: "Heavyweight pullover & zip-up hoodies.", productType: PRODUCT_TYPES.APPAREL_SINGLE },
            { slug: "sweatpants", name: "Sweatpants", blurb: "Joggers and open-bottom sweats.", productType: PRODUCT_TYPES.APPAREL_SINGLE },
        ],
    },
    {
        slug: "stickers",
        name: "Stickers",
        tagline: "Stick anywhere. Stick forever.",
        image: IMG_STICKERS,
        description:
            "Weatherproof full-color stickers — sold singly or in packs. Every artwork imaginable, cut sharp and sealed to survive the elements.",
        sub: [
            { slug: "sticker-packs", name: "Sticker Packs", blurb: "Full-color multi-sticker packs.", productType: PRODUCT_TYPES.STICKER_PACK },
            { slug: "die-cut", name: "Die-Cut", blurb: "Single custom-shape stickers.", productType: PRODUCT_TYPES.SINGLE_ITEM },
            { slug: "bumper-stickers", name: "Bumper Stickers", blurb: "Tough outdoor bumper stickers.", productType: PRODUCT_TYPES.SINGLE_ITEM },
            { slug: "misc-stickers", name: "Miscellaneous", blurb: "Everything else that sticks.", productType: PRODUCT_TYPES.SINGLE_ITEM },
        ],
    },
    {
        slug: "vinyl-decals",
        name: "Vinyl Decals",
        tagline: "Cut sharp. Built to last.",
        image: IMG_STICKERS,
        description:
            "Precision-cut vinyl decals for cars, windows, walls, and gear. Single-color or layered, weatherproof, applied to just about anything.",
        sub: [
            { slug: "car-decals", name: "Car Decals", blurb: "Window clings, hood graphics, racing stripes.", productType: PRODUCT_TYPES.SINGLE_ITEM },
            { slug: "window-decals", name: "Window Decals", blurb: "Storefront & home window vinyl.", productType: PRODUCT_TYPES.SINGLE_ITEM },
            { slug: "wall-decals", name: "Wall Decals", blurb: "Indoor wall art & lettering.", productType: PRODUCT_TYPES.SINGLE_ITEM },
            { slug: "misc-decals", name: "Miscellaneous", blurb: "Custom cut vinyl of every kind.", productType: PRODUCT_TYPES.SINGLE_ITEM },
        ],
    },
    {
        slug: "buttons",
        name: "Buttons",
        tagline: "Small canvas. Big statement.",
        image: IMG_BUTTONS,
        description:
            "Pinback buttons in every size. Bands, brands, events, statements — pressed sharp, sealed clear.",
        sub: [],
        productType: PRODUCT_TYPES.SINGLE_ITEM,
    },
    {
        slug: "woodworking",
        name: "Woodworking",
        tagline: "Hand-built, storm-tested.",
        image: IMG_WOODWORK,
        description:
            "Hand-built signs, bottle cap openers, key hooks, shelves, and one-of-one builds. Walnut, oak, maple — finished sharp.",
        sub: [
            { slug: "signs", name: "Signs", blurb: "Indoor & outdoor signage.", productType: PRODUCT_TYPES.WOODWORKING },
            { slug: "bottle-openers", name: "Bottle Cap Openers", blurb: "Wall-mounted wood cap catchers.", productType: PRODUCT_TYPES.WOODWORKING },
            { slug: "key-hooks", name: "Key Hooks", blurb: "Hand-crafted key racks & wall hooks.", productType: PRODUCT_TYPES.WOODWORKING },
            { slug: "shelves", name: "Shelves", blurb: "Floating shelves, mantles, display ledges.", productType: PRODUCT_TYPES.WOODWORKING },
            { slug: "misc-wood", name: "Miscellaneous", blurb: "Custom wood builds of every kind.", productType: PRODUCT_TYPES.WOODWORKING },
        ],
    },
    {
        slug: "laser-engraving",
        name: "Laser Engraving",
        tagline: "Precision. Permanent.",
        image: IMG_LASER,
        description:
            "High-resolution laser engraving on mirrors, glass and just about anything else. Logos, photos, text — your design, etched in.",
        sub: [
            { slug: "mirrors", name: "Mirrors", blurb: "Engraved mirrors, custom shapes & sizes.", productType: PRODUCT_TYPES.LASER_ENGRAVING },
            { slug: "glass", name: "Glass", blurb: "Glassware, panels, awards & gifts.", productType: PRODUCT_TYPES.LASER_ENGRAVING },
            { slug: "misc-laser", name: "Miscellaneous", blurb: "Custom engraving on anything you bring.", productType: PRODUCT_TYPES.LASER_ENGRAVING },
        ],
    },
    {
        slug: "collectibles",
        name: "Collectibles",
        tagline: "Limited runs. Storm-forged.",
        image: IMG_BUTTONS,
        description:
            "Limited-run and one-of-one collectible pieces — the special drops that don't fit anywhere else.",
        sub: [],
        productType: PRODUCT_TYPES.SINGLE_ITEM,
    },
];

export function findCategory(slug) {
    return CATEGORIES.find((c) => c.slug === slug);
}

export function findSub(catSlug, subSlug) {
    const cat = findCategory(catSlug);
    if (!cat) return null;
    return cat.sub.find((s) => s.slug === subSlug) || null;
}

export function getProductType(catSlug, subSlug) {
    const sub = findSub(catSlug, subSlug);
    if (sub && sub.productType) return sub.productType;
    const cat = findCategory(catSlug);
    if (cat && cat.productType) return cat.productType;
    return PRODUCT_TYPES.SINGLE_ITEM;
}

// Public collections (themes) shown on product listing pages
export const COLLECTIONS = [
    { slug: "pokemon", name: "Pokémon" },
    { slug: "video-games", name: "Video Games" },
    { slug: "superheroes", name: "Superheroes" },
    { slug: "nostalgia", name: "Nostalgia" },
    { slug: "adult", name: "Adult" },
];

export const ADULT_SIZES = [
    { code: "S",  label: "S",  upcharge: 0 },
    { code: "M",  label: "M",  upcharge: 0 },
    { code: "L",  label: "L",  upcharge: 0 },
    { code: "XL", label: "XL", upcharge: 0 },
    { code: "2X", label: "2X (+$2)", upcharge: 2 },
    { code: "3X", label: "3X (+$4)", upcharge: 4 },
    { code: "4X", label: "4X (+$6)", upcharge: 6 },
];
export const ADULT_BASE_PRICE = 17;

export const KIDS_SIZES = [
    { code: "S",  label: "S",  upcharge: 0 },
    { code: "M",  label: "M",  upcharge: 0 },
    { code: "L",  label: "L",  upcharge: 0 },
    { code: "XL", label: "XL", upcharge: 0 },
];
export const KIDS_BASE_PRICE = 15;

export const CART_STORAGE_KEY = "dsc:cart";
export const ORDER_STORAGE_KEY = "dsc:pending-order";
